import { useCallback, useEffect, useMemo, useState } from "react";
import {
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
} from "date-fns";
import { zhCN } from "date-fns/locale";
import { CalendarDays, History, List } from "lucide-react";
import {
  AdminAlert,
  AdminEmptyState,
  AdminList,
  AdminListRow,
  AdminPill,
} from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ApiError } from "@/lib/api";
import { createKbApi, type KbRawSource } from "@/lib/kb-api";
import { KB_COPY } from "@/lib/kb-copy";
import {
  rawSourceDisplayTitle,
  rawSourceStatusLabel,
  rawSourceStatusTone,
} from "@/lib/kb-raw-source-status";
import { cn } from "@/lib/utils";

type InboxView = "list" | "calendar" | "timeline";

type InboxChangeLogProps = {
  accountId: string;
  /** 当前栈 Wiki；列表可按 wiki 过滤 */
  wikiId?: number | null;
  scope: "enterprise" | "brand";
};

function groupByDay(sources: KbRawSource[]): { dayKey: string; day: Date; items: KbRawSource[] }[] {
  const map = new Map<string, { day: Date; items: KbRawSource[] }>();
  for (const raw of sources) {
    const day = startOfDay(parseISO(raw.created_at));
    const dayKey = format(day, "yyyy-MM-dd");
    const bucket = map.get(dayKey);
    if (bucket) {
      bucket.items.push(raw);
    } else {
      map.set(dayKey, { day, items: [raw] });
    }
  }
  return Array.from(map.entries())
    .map(([dayKey, value]) => ({ dayKey, ...value }))
    .sort((a, b) => b.day.getTime() - a.day.getTime());
}

function StatusPill({ status }: { status: string }) {
  return <AdminPill tone={rawSourceStatusTone(status)}>{rawSourceStatusLabel(status)}</AdminPill>;
}

function SourceMeta({ raw }: { raw: KbRawSource }) {
  return (
    <div className="text-xs text-muted-foreground">
      {raw.content_type || "文本"} · {format(parseISO(raw.created_at), "yyyy年M月d日 HH:mm", { locale: zhCN })}
    </div>
  );
}

function SourceRow({ raw }: { raw: KbRawSource }) {
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <div className="font-medium">{rawSourceDisplayTitle(raw)}</div>
        <SourceMeta raw={raw} />
        {raw.ingest_error ? (
          <p className="text-xs text-destructive">{raw.ingest_error}</p>
        ) : null}
      </div>
      <StatusPill status={raw.status} />
    </div>
  );
}

export function InboxChangeLog({ accountId, wikiId = null, scope }: InboxChangeLogProps) {
  const kb = useMemo(() => createKbApi(accountId), [accountId]);
  const [rawSources, setRawSources] = useState<KbRawSource[]>([]);
  const [view, setView] = useState<InboxView>("list");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => new Date());

  const inboxTitle =
    scope === "enterprise" ? KB_COPY.enterpriseInboxTitle : KB_COPY.brandInboxTitle;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = await kb.listRawSources(wikiId ?? undefined);
      setRawSources(payload.raw_sources);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载知识旅程");
    } finally {
      setIsLoading(false);
    }
  }, [kb, wikiId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () =>
      [...rawSources].sort(
        (a, b) => parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime(),
      ),
    [rawSources],
  );

  const dayGroups = useMemo(() => groupByDay(sorted), [sorted]);
  const sourceDates = useMemo(
    () => sorted.map((raw) => startOfDay(parseISO(raw.created_at))),
    [sorted],
  );

  const selectedDayItems = useMemo(() => {
    if (!selectedDay) return [];
    return sorted.filter((raw) => isSameDay(parseISO(raw.created_at), selectedDay));
  }, [selectedDay, sorted]);

  const monthDayGroups = useMemo(
    () => dayGroups.filter((group) => isSameMonth(group.day, calendarMonth)),
    [dayGroups, calendarMonth],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 space-y-3 border-b border-border px-5 py-4">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{inboxTitle}</h2>
          <p className="max-w-2xl text-sm text-muted-foreground">{KB_COPY.inboxDescription}</p>
        </div>

        <ButtonGroup aria-label="知识旅程视图">
          {(
            [
              { id: "list" as const, label: KB_COPY.inboxViewList, icon: List },
              { id: "calendar" as const, label: KB_COPY.inboxViewCalendar, icon: CalendarDays },
              { id: "timeline" as const, label: KB_COPY.inboxViewTimeline, icon: History },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={view === id ? "default" : "outline"}
              className="gap-1.5"
              aria-pressed={view === id}
              onClick={() => setView(id)}
            >
              <Icon className="size-3.5" />
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </header>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-5 p-5">
          {error ? <AdminAlert>{error}</AdminAlert> : null}

          {isLoading ? (
            <p className="text-sm text-muted-foreground">正在加载资料记录…</p>
          ) : sorted.length === 0 ? (
            <AdminEmptyState>{KB_COPY.inboxEmpty}</AdminEmptyState>
          ) : view === "list" ? (
            <AdminList>
              {sorted.map((raw) => (
                <AdminListRow key={raw.id}>
                  <SourceRow raw={raw} />
                </AdminListRow>
              ))}
            </AdminList>
          ) : view === "calendar" ? (
            <div className="grid gap-5 lg:grid-cols-[auto_1fr]">
              <section className="h-fit rounded-lg border border-border bg-card p-3">
                <Calendar
                  mode="single"
                  locale={zhCN}
                  selected={selectedDay}
                  onSelect={setSelectedDay}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  modifiers={{ hasSource: sourceDates }}
                  modifiersClassNames={{
                    hasSource:
                      "relative after:absolute after:bottom-1 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rounded-full after:bg-primary",
                  }}
                  className="rounded-md"
                />
                <p className="mt-2 px-1 text-xs text-muted-foreground">
                  圆点表示有资料的日期；点选可看当天记录。
                </p>
              </section>

              <section className="min-w-0 space-y-3">
                <h3 className="text-sm font-medium">
                  {selectedDay
                    ? format(selectedDay, "yyyy年M月d日 EEEE", { locale: zhCN })
                    : format(calendarMonth, "yyyy年M月", { locale: zhCN }) + " · 按日分组"}
                </h3>
                {selectedDay ? (
                  selectedDayItems.length === 0 ? (
                    <AdminEmptyState>{KB_COPY.inboxEmptyDay}</AdminEmptyState>
                  ) : (
                    <AdminList>
                      {selectedDayItems.map((raw) => (
                        <AdminListRow key={raw.id}>
                          <SourceRow raw={raw} />
                        </AdminListRow>
                      ))}
                    </AdminList>
                  )
                ) : monthDayGroups.length === 0 ? (
                  <AdminEmptyState>本月还没有资料记录。</AdminEmptyState>
                ) : (
                  <div className="space-y-4">
                    {monthDayGroups.map((group) => (
                      <div key={group.dayKey} className="space-y-2">
                        <button
                          type="button"
                          className="text-xs font-medium text-muted-foreground hover:text-foreground"
                          onClick={() => setSelectedDay(group.day)}
                        >
                          {format(group.day, "M月d日 EEE", { locale: zhCN })} · {group.items.length}{" "}
                          条
                        </button>
                        <AdminList>
                          {group.items.map((raw) => (
                            <AdminListRow key={raw.id}>
                              <SourceRow raw={raw} />
                            </AdminListRow>
                          ))}
                        </AdminList>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          ) : (
            <ol className="relative space-y-0">
              {dayGroups.map((group, groupIndex) => (
                <li key={group.dayKey} className="relative">
                  <div className="sticky top-0 z-1 mb-3 bg-card/95 py-1 backdrop-blur-sm">
                    <time
                      dateTime={group.dayKey}
                      className="text-xs font-medium tabular-nums text-muted-foreground"
                    >
                      {format(group.day, "yyyy年M月d日 EEEE", { locale: zhCN })}
                    </time>
                  </div>
                  <ul className="space-y-0 pb-6">
                    {group.items.map((raw, index) => {
                      const isLast =
                        groupIndex === dayGroups.length - 1 && index === group.items.length - 1;
                      return (
                        <li key={raw.id} className="relative flex gap-4 pb-5 last:pb-1">
                          {!isLast ? (
                            <span
                              className="absolute top-6 bottom-0 left-[11px] w-px bg-border"
                              aria-hidden
                            />
                          ) : null}
                          <div
                            className={cn(
                              "relative z-10 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border bg-background",
                              raw.status === "ingested" && "border-emerald-500/40 text-emerald-600",
                              raw.status === "queued" && "border-amber-500/40 text-amber-600",
                              raw.status === "failed" && "border-destructive/40 text-destructive",
                            )}
                          >
                            <span className="size-1.5 rounded-full bg-current" />
                          </div>
                          <div className="min-w-0 flex-1 space-y-1.5 rounded-lg border border-border bg-card px-4 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <time
                                dateTime={raw.created_at}
                                className="text-xs tabular-nums text-muted-foreground"
                              >
                                {format(parseISO(raw.created_at), "HH:mm", { locale: zhCN })}
                              </time>
                              <StatusPill status={raw.status} />
                            </div>
                            <div className="text-sm font-medium leading-snug">
                              {rawSourceDisplayTitle(raw)}
                            </div>
                            <SourceMeta raw={raw} />
                            {raw.ingest_error ? (
                              <p className="text-xs text-destructive">{raw.ingest_error}</p>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ol>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
